import useDefaultToast from '@/components/useDefaultToast'
import {
  resetInterruptedState,
  selectConversation,
  selectConversationStarted,
  selectLastUserMessage,
  setBackendConversationId,
  upsertMessage as upsertMessageAction,
  UpsertMessagePayload,
} from '@/redux/slices/aiChatSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { nanoid } from '@reduxjs/toolkit'
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import AIProcessor from './processor'
import { AIProcessorOptions, ClientCommand, UTMParams } from './types'

export type AIConnectionRef<
  ExtraStepEvents extends object = object,
  ExtraCustomEvents extends object = object,
  ExtraClientCommand extends object = object,
> = {
  currentMessageId: string
  processor: AIProcessor<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>
}

type AIConnectionProps<
  ExtraStepEvents extends object,
  ExtraCustomEvents extends object,
  ExtraClientCommand extends object,
> = {
  ref: React.Ref<AIConnectionRef<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>>
  conversationId: string
  channelName: string
  utmParams: UTMParams
  extraMessageParams?: Record<string, any>
}

export default function AIConnection<
  ExtraStepEvents extends object,
  ExtraCustomEvents extends object,
  ExtraClientCommand extends object,
>({
  ref,
  conversationId,
  channelName,
  utmParams,
  extraMessageParams,
}: AIConnectionProps<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>) {
  const { t } = useTranslation('aiChat')

  const processorRef = useRef<AIProcessor<ExtraStepEvents, ExtraCustomEvents, ClientCommand> | null>(null)

  const dispatch = useAppDispatch()
  const toast = useDefaultToast()

  const lastUserMessage = useAppSelector(state => selectLastUserMessage(state, conversationId))
  const interrupted = useAppSelector(state => selectConversation(state.aiChat, conversationId)?.interrupted)

  const conversationExists = useAppSelector(state => !!selectConversation(state.aiChat, conversationId))
  const conversationReset = useAppSelector(state => !selectConversationStarted(state, conversationId))

  const backendConversationId = useAppSelector(
    state => selectConversation(state.aiChat, conversationId)?.backendConversationId
  )

  const backendConversationIdRef = useRef<string | undefined>(undefined)
  backendConversationIdRef.current = backendConversationId
  const currentAIMessageIdRef = useRef('')

  const upsertMessage = useCallback(
    (payload: Omit<UpsertMessagePayload, 'id'>, newMessageId?: string) => {
      if (newMessageId) currentAIMessageIdRef.current = newMessageId
      dispatch(upsertMessageAction({ conversationId, message: { ...payload, id: currentAIMessageIdRef.current } }))
    },
    [dispatch, conversationId]
  )

  useEffect(() => {
    if (!lastUserMessage) return

    processorRef.current?.perform('message', {
      attachment_ids: lastUserMessage.parts.filter(part => part.type === 'attachment').map(part => part.attachment.id),
      user_input: lastUserMessage.parts
        .filter(part => part.type === 'text')
        .map(item => item.content)
        .join('\n'),
      conversation_id: backendConversationIdRef.current,
      utm_device: utmParams.device,
      utm_source: utmParams.source,
      utm_medium: utmParams.medium,
      ...extraMessageParams,
    })

    upsertMessage({ role: 'assistant' }, nanoid())
    processorRef.current?.prepare()
  }, [lastUserMessage, upsertMessage, utmParams.device, utmParams.source, utmParams.medium, extraMessageParams])

  useEffect(() => {
    if (!interrupted) return

    dispatch(resetInterruptedState({ conversationId }))
    processorRef.current?.forceInterrupt()
  }, [dispatch, interrupted, conversationId])

  useEffect(() => {
    if (!conversationExists) return

    if (conversationReset) {
      dispatch(setBackendConversationId({ conversationId, backendConversationId: undefined }))
      currentAIMessageIdRef.current = ''
      processorRef.current?.reset()
    }
  }, [conversationExists, conversationId, conversationReset, dispatch])

  useLayoutEffect(() => {
    const options: AIProcessorOptions = {
      channelName,
      channelToken: conversationId,
      onPerformFail: () => {
        toast.show(t('networkError'))
      },
      onMessageStarted: backendConversationId => {
        dispatch(setBackendConversationId({ conversationId, backendConversationId }))
      },
      getBackendConversationId: () => backendConversationIdRef.current,
      upsertMessage,
      reportError: error => {
        let message = t(`stepErrorMessages.${error.step}`, { defaultValue: '' })
        if (!message) {
          const stepName = t(`stepNames.${error.step}`, { defaultValue: '' })
          message = stepName ? t('errorOccurredWithStep', { step: stepName }) : t('errorOccurred')
        }
        upsertMessage({ chunks: [{ key: `error-${nanoid()}`, type: 'error', message }] })
      },
    }

    const processor = new AIProcessor(options)
    processorRef.current = processor

    return () => {
      processor.forceInterrupt()
      processor.destroy()
    }
  }, [channelName, conversationId, dispatch, toast, t, upsertMessage])

  useImperativeHandle(
    ref,
    () =>
      ({
        get currentMessageId() {
          return currentAIMessageIdRef.current
        },
        get processor() {
          return processorRef.current as AIProcessor<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>
        },
      }) satisfies AIConnectionRef<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>,
    []
  )

  return null
}
